// ═══════════════════════════════════════════════════════════════════════════
// FeaturedTab — admin/manager curation for the home "Staff Picks" row.
//
// Lists the current picks (reorder / edit note / remove / show-hide) and lets
// a curator search PUBLIC episodes and feature them. All writes go through the
// can_curate-gated RPCs in migration 070, so a non-curator can't mutate even
// if they reach this UI. Mounts inside AdminPage's tab strip.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import {
  listCuratorPicks, addCuratorPick, removeCuratorPick,
  updateCuratorPick, searchFeaturableEpisodes,
} from '../../lib/curation';

export default function FeaturedTab() {
  const toast = useToast();
  const [picks, setPicks]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery]   = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setPicks(await listCuratorPicks()); }
    catch (e) { toast.error(e?.message || 'Could not load Staff Picks.'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { refresh(); }, [refresh]);

  const runSearch = useCallback(async () => {
    setSearching(true);
    try { setResults(await searchFeaturableEpisodes(query)); }
    finally { setSearching(false); }
  }, [query]);

  useEffect(() => {
    const t = setTimeout(runSearch, 300); // debounce
    return () => clearTimeout(t);
  }, [runSearch]);

  const featuredIds = new Set(picks.filter(p => p.is_active).map(p => p.episode_id));

  async function doAdd(ep) {
    setBusyId(ep.id);
    try {
      await addCuratorPick(ep.id, '');
      toast.success('Added to Staff Picks.');
      await refresh();
    } catch (e) { toast.error(e?.message === 'already_featured' ? 'Already featured.' : (e?.message || 'Could not add.')); }
    finally { setBusyId(null); }
  }
  async function doRemove(pick) {
    setBusyId(pick.id);
    try { await removeCuratorPick(pick.id); await refresh(); }
    catch (e) { toast.error(e?.message || 'Could not remove.'); }
    finally { setBusyId(null); }
  }
  async function doToggle(pick) {
    setBusyId(pick.id);
    try { await updateCuratorPick(pick.id, { is_active: !pick.is_active }); await refresh(); }
    catch (e) { toast.error(e?.message || 'Could not update.'); }
    finally { setBusyId(null); }
  }
  async function doNote(pick, note) {
    if (note === (pick.note || '')) return;
    try { await updateCuratorPick(pick.id, { note }); }
    catch (e) { toast.error(e?.message || 'Could not save note.'); }
  }
  // Move up/down by swapping positions with the adjacent pick.
  async function doMove(idx, dir) {
    const a = picks[idx];
    const b = picks[idx + dir];
    if (!a || !b) return;
    setBusyId(a.id);
    try {
      await Promise.all([
        updateCuratorPick(a.id, { position: b.position }),
        updateCuratorPick(b.id, { position: a.position }),
      ]);
      await refresh();
    } catch (e) { toast.error(e?.message || 'Could not reorder.'); }
    finally { setBusyId(null); }
  }

  return (
    <div className="mkt-panel">
      <h2 className="mkt-panel-title">Staff Picks</h2>
      <p className="mkt-panel-sub">Curate the home page “Staff Picks” row. Only public episodes can be featured.</p>

      {/* ── Current picks ── */}
      {loading ? (
        <div className="mkt-empty">Loading…</div>
      ) : picks.length === 0 ? (
        <div className="mkt-empty">No picks yet. Search below and feature an episode.</div>
      ) : (
        <div className="feat-list">
          {picks.map((pick, idx) => (
            <div key={pick.id} className={`feat-row${pick.is_active ? '' : ' feat-row--inactive'}`}>
              <div className="feat-order">
                <button type="button" className="feat-move" disabled={idx === 0 || busyId} onClick={() => doMove(idx, -1)} aria-label="Move up">▲</button>
                <button type="button" className="feat-move" disabled={idx === picks.length - 1 || busyId} onClick={() => doMove(idx, 1)} aria-label="Move down">▼</button>
              </div>
              <div className="feat-main">
                <div className="feat-title">{pick.title || pick.topic || 'Untitled episode'}</div>
                <div className="feat-creator">{pick.full_name || (pick.username ? `@${pick.username}` : 'Unknown creator')}</div>
                <input
                  className="feat-note"
                  defaultValue={pick.note || ''}
                  placeholder="Curator note (optional)"
                  maxLength={200}
                  onBlur={(e) => doNote(pick, e.target.value)}
                />
              </div>
              <div className="feat-actions">
                <button type="button" className="feat-btn" disabled={busyId} onClick={() => doToggle(pick)}>
                  {pick.is_active ? 'Hide' : 'Show'}
                </button>
                <button type="button" className="feat-btn feat-btn--danger" disabled={busyId} onClick={() => doRemove(pick)}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Add ── */}
      <h3 className="feat-add-title">Feature an episode</h3>
      <input
        className="mkt-search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search public episodes by topic…"
      />
      <div className="feat-results">
        {searching && <div className="mkt-empty">Searching…</div>}
        {!searching && results.length === 0 && <div className="mkt-empty">No public episodes match.</div>}
        {results.map((ep) => {
          const featured = featuredIds.has(ep.id);
          return (
            <div key={ep.id} className="feat-result">
              <div className="feat-result-title">{ep.result?.title || ep.topic || 'Untitled episode'}</div>
              <button
                type="button"
                className="feat-btn"
                disabled={featured || busyId === ep.id}
                onClick={() => doAdd(ep)}
              >
                {featured ? 'Featured' : (busyId === ep.id ? 'Adding…' : 'Feature')}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
